import { useContext, useState, useEffect, useRef, FC, ChangeEvent } from "react"
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker"
import { Icon } from "source/shared/components"
import { WebBootContext } from "source/publishing/WebBoot"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { useCurrentOrganizationTimeZone } from "source/calendar/hooks/useCurrentOrganizationTimeZone"

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

interface WebBootContextType {
  currentOrganization: {
    attributes: {
      datetime_fmt_configuration: {
        dateFirst: boolean
      }
    }
  }
}

const HTML5_DATE_FORMAT = "YYYY-MM-DD"

interface DatePickerProps
  extends Omit<ReactDatePickerProps, "selected" | "onChange" | "value"> {
  value?: string | Date | null
  onChange?: (event: { target: { value: string } }) => void
  showOnLoad?: boolean
  errorClass?: string
  maxCap?: number
  minCap?: number
  placeholder?: string
  popperClassName?: string
}

const yearsSince1885 = new Date().getFullYear() - 1885

function parseDate(
  value: string | Date | null | undefined,
  dateFormat: string,
) {
  if (!value) return null

  // If value is already a Date object, return it directly
  if (value instanceof Date) {
    return isFinite(value.getTime()) ? value : null
  }

  try {
    // We use this to parse dates without times (birthdays, anniversaries),
    // which we can parse as local dates
    // without timezone conversion to avoid off-by-one errors
    // customParseFormat plugin supports array of formats
    const parsed = dayjs(value, [dateFormat, HTML5_DATE_FORMAT], true)

    if (parsed.isValid()) {
      // Create a local date using the parsed year, month, and day
      // This avoids timezone conversion issues
      const localDate = new Date(parsed.year(), parsed.month(), parsed.date())
      if (isFinite(localDate.getTime())) return localDate
    }
  } catch (e) {
    console.warn("Failed to parse date value:", value, e)
  }
  return null
}

export const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  showOnLoad = false,
  errorClass,
  maxCap = 20,
  minCap = yearsSince1885,
  placeholder,
  popperClassName,
  ...rest
}) => {
  const {
    currentOrganization: {
      attributes: { datetime_fmt_configuration },
    },
  } = useContext(WebBootContext) as WebBootContextType
  const inputRef = useRef<ReactDatePicker>(null)
  const timeZone = useCurrentOrganizationTimeZone()

  // Parse initial value according to org date format, fallback to YYYY-MM-DD
  const dateFormat = datetime_fmt_configuration.dateFirst
    ? "DD/MM/YYYY"
    : "MM/DD/YYYY"
  const [selectedDate, setSelectedDate] = useState(() =>
    parseDate(value, dateFormat),
  )

  const currentYear = new Date().getFullYear()
  const minDate = new Date(currentYear - minCap, 0, 1)
  const maxDate = new Date(currentYear + maxCap, 11, 31)

  // Update selectedDate when value prop changes
  useEffect(() => {
    if (!value) {
      setSelectedDate(null)
      return
    }

    const parsedDate = parseDate(value, dateFormat)
    if (parsedDate) setSelectedDate(parsedDate)
  }, [value, dateFormat, timeZone])

  useEffect(() => {
    if (showOnLoad && inputRef.current) {
      inputRef.current.setFocus()
    }
  }, [showOnLoad])

  const handleChange = (date: Date | null) => {
    setSelectedDate(date)
    if (onChange) {
      const localDate = date
        ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
        : null
      const formattedDate = localDate
        ? dayjs(localDate).format("YYYY-MM-DD")
        : ""
      const fakeEvent = { target: { value: formattedDate } }
      onChange(fakeEvent)
    }
  }

  // Handle specific cases where user types dates in a format that doesn't use slashes
  // we want the raw value to avoid React datepicker UTC parsing
  const handleRawChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setTimeout(() => {
        const [year, month, day] = value.split("-").map(Number)
        const localDate = new Date(year, month - 1, day)
        setSelectedDate(localDate)
        if (onChange) {
          const formattedDate = dayjs(localDate).format("YYYY-MM-DD")
          const fakeEvent = { target: { value: formattedDate } }
          onChange(fakeEvent)
        }
      }, 0)
    }

    // Handle MM-DD-YYYY  and DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
      setTimeout(() => {
        let localDate
        if (datetime_fmt_configuration.dateFirst) {
          const [day, month, year] = value.split("-").map(Number)
          localDate = new Date(year, month - 1, day)
        } else {
          const [month, day, year] = value.split("-").map(Number)
          localDate = new Date(year, month - 1, day)
        }

        setSelectedDate(localDate)
        if (onChange) {
          const formattedDate = dayjs(localDate).format("YYYY-MM-DD")
          const fakeEvent = { target: { value: formattedDate } }
          onChange(fakeEvent)
        }
      }, 0)
    }

    // Handle 4 to 8-digit format without separators (MMDDYYYY or DDMMYYYY)
    // If only 4 digits are entered, it will assume those were the MMDD or DDMM, and fill in the current year
    if (/^\d{4,8}$/.test(value)) {
      setTimeout(() => {
        let localDate
        const yearGiven = parseInt(value.substring(4, 8), 10)
        const year = Number.isNaN(yearGiven) ? currentYear : yearGiven

        if (datetime_fmt_configuration.dateFirst) {
          const day = parseInt(value.substring(0, 2), 10)
          const month = parseInt(value.substring(2, 4), 10)
          localDate = new Date(year, month - 1, day)
        } else {
          const month = parseInt(value.substring(0, 2), 10)
          const day = parseInt(value.substring(2, 4), 10)
          localDate = new Date(year, month - 1, day)
        }

        setSelectedDate(localDate)
        if (onChange) {
          const formattedDate = dayjs(localDate).format("YYYY-MM-DD")
          const fakeEvent = { target: { value: formattedDate } }
          onChange(fakeEvent)
        }
      }, 0)
    }
  }

  return (
    <div className={`date-field ${errorClass || ""}`}>
      <span className="date-field__icon">
        <Icon symbol="general#calendar" title="Date" />
      </span>
      <ReactDatePicker
        ref={inputRef}
        selected={selectedDate}
        onChange={handleChange}
        onChangeRaw={handleRawChange}
        dateFormat={
          datetime_fmt_configuration.dateFirst ? "dd/MM/yyyy" : "MM/dd/yyyy"
        }
        minDate={minDate}
        maxDate={maxDate}
        showYearDropdown
        showMonthDropdown
        showPopperArrow={false}
        dropdownMode="select"
        wrapperClassName="w-100%"
        placeholderText={
          placeholder ||
          (datetime_fmt_configuration.dateFirst ? "DD/MM/YYYY" : "MM/DD/YYYY")
        }
        popperClassName={popperClassName ?? ""}
        {...rest}
      />
    </div>
  )
}
